import { useCallback, useEffect, useMemo, useState } from "react";
import { MobileAppsMessage, XolaMobileApp } from "types";
import { useDevice } from "./useSystemInfo";

export type Message = {
    type: string;
    payload?: Record<string, unknown>;
    context?: XolaMobileApp[];
};

const isValidMobileAppMessage = (data: unknown): data is Message => {
    if (typeof data !== "object" || data === null) return false;
    const message = data as Record<string, unknown>;
    if (typeof message.type !== "string") return false;
    if (message.payload !== undefined && (typeof message.payload !== "object" || message.payload === null))
        return false;
    if (message.context !== undefined && !Array.isArray(message.context)) return false;
    return true;
};

const postMessageToNativeApp = (message: MobileAppsMessage) => {
    if (
        typeof window !== "undefined" &&
        window.ReactNativeWebView &&
        typeof window.ReactNativeWebView.postMessage === "function"
    ) {
        const payload = JSON.stringify(message);
        window.ReactNativeWebView.postMessage(payload);
    }
};

export const useXolaMobileAppsBridge = () => {
    const { xolaMobileApp, isXolaMobileApp } = useDevice();
    const [mobileAppMessage, setMobileAppMessage] = useState<Message>();
    const sendMessage = useCallback((message: MobileAppsMessage) => {
        postMessageToNativeApp(message);
    }, []);

    useEffect(() => {
        if (!isXolaMobileApp) {
            return;
        }

        const handleMessage = (event: MessageEvent) => {
            let data = event.data;
            try {
                data = typeof data === "string" ? JSON.parse(data) : data;
            } catch {
                // Not JSON, ignore
                return;
            }

            if (isValidMobileAppMessage(data)) {
                setMobileAppMessage(data);
            }
        };

        window.addEventListener("message", handleMessage);
        return () => {
            window.removeEventListener("message", handleMessage);
        };
    }, [isXolaMobileApp]);

    return useMemo(
        () => ({
            xolaMobileApp,
            isXolaMobileApp,
            sendMessage,
            mobileAppMessage,
        }),
        [xolaMobileApp, isXolaMobileApp, sendMessage, mobileAppMessage],
    );
};
